import { useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Props {
  content: string;
  /** Optional base font size (px). Defaults to 13 to match chat body text. */
  fontSize?: number;
}

export function MarkdownContent({ content, fontSize = 13 }: Props) {
  return (
    <div
      style={{
        fontSize,
        color: 'var(--text)',
        lineHeight: 1.55,
        wordWrap: 'break-word',
        minWidth: 0,
      }}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--accent)', textDecoration: 'underline' }}
            >
              {children}
            </a>
          ),
          h1: ({ children }) => (
            <h1 style={{ fontSize: fontSize + 2, fontWeight: 600, margin: '10px 0 4px' }}>{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 style={{ fontSize: fontSize + 1, fontWeight: 600, margin: '8px 0 4px' }}>{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 style={{ fontSize, fontWeight: 600, margin: '6px 0 2px' }}>{children}</h3>
          ),
          p: ({ children }) => <p style={{ margin: '0 0 6px' }}>{children}</p>,
          ul: ({ children }) => (
            <ul style={{ margin: '0 0 6px', paddingLeft: 20 }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '0 0 6px', paddingLeft: 20 }}>{children}</ol>
          ),
          li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
          code: ({ children, className }) => {
            if (className) {
              // Block-level code is wrapped in <pre> by react-markdown — see pre() below.
              return (
                <code
                  className={className}
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    fontSize: fontSize - 1,
                    whiteSpace: 'pre',
                  }}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                style={{
                  background: 'var(--bg-alt2)',
                  border: '1px solid var(--border)',
                  borderRadius: 3,
                  padding: '1px 5px',
                  fontSize: fontSize - 1,
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                }}
              >
                {children}
              </code>
            );
          },
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
          table: ({ children }) => (
            <div
              style={{
                overflowX: 'auto',
                margin: '6px 0',
                border: '1px solid var(--border)',
                borderRadius: 6,
              }}
            >
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: fontSize - 1 }}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead style={{ background: 'var(--bg-alt)' }}>{children}</thead>
          ),
          th: ({ children }) => (
            <th
              style={{
                textAlign: 'left',
                padding: '6px 8px',
                borderBottom: '1px solid var(--border-strong)',
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td
              style={{
                padding: '6px 8px',
                borderTop: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              {children}
            </td>
          ),
          blockquote: ({ children }) => (
            <blockquote
              style={{
                margin: '6px 0',
                paddingLeft: 10,
                borderLeft: '3px solid var(--accent-soft)',
                color: 'var(--text-soft)',
              }}
            >
              {children}
            </blockquote>
          ),
          hr: () => (
            <hr
              style={{
                border: 0,
                borderTop: '1px solid var(--border)',
                margin: '10px 0',
              }}
            />
          ),
        }}
      >
        {content}
      </Markdown>
    </div>
  );
}

/**
 * Fenced code block with a language label strip + copy button.
 * react-markdown routes fenced blocks through `pre > code.language-xxx`, so we
 * receive the inner `<code>` as the only child and unpack it to read className.
 */
function CodeBlock({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const { language, code } = unpackCodeChild(children);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* Clipboard can fail in iframes without the permission — swallow. */
    }
  };

  return (
    <div
      style={{
        margin: '8px 0',
        border: '1px solid var(--border)',
        borderRadius: 6,
        overflow: 'hidden',
        background: 'var(--bg-alt2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px 4px 10px',
          background: 'var(--bg-alt)',
          borderBottom: '1px solid var(--border)',
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
        }}
      >
        <span>{language || 'code'}</span>
        <button
          type="button"
          onClick={onCopy}
          style={{
            fontSize: 10,
            fontFamily: 'inherit',
            fontWeight: 500,
            padding: '2px 8px',
            border: '1px solid var(--border)',
            borderRadius: 4,
            background: 'var(--surface)',
            color: copied ? 'var(--accent)' : 'var(--text-soft)',
            cursor: 'pointer',
            textTransform: 'none',
            letterSpacing: 0,
          }}
          aria-label="Copy code"
        >
          {copied ? '✓ copied' : 'copy'}
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '8px 10px',
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          fontSize: 12,
          lineHeight: 1.45,
          color: 'var(--text)',
          overflowX: 'auto',
          whiteSpace: 'pre',
        }}
      >
        {children}
      </pre>
    </div>
  );
}

/**
 * Unpack the single `<code>` child react-markdown passes into `pre` so we can
 * extract the fence language (className=`language-ts`) and the raw source for
 * the copy button.
 */
function unpackCodeChild(children: React.ReactNode): { language: string; code: string } {
  if (children && typeof children === 'object' && 'props' in children) {
    const props = (children as { props?: { className?: string; children?: React.ReactNode } }).props ?? {};
    const cls = typeof props.className === 'string' ? props.className : '';
    const match = cls.match(/language-([\w-]+)/);
    const language = match ? match[1]! : '';
    const inner = props.children;
    const code = typeof inner === 'string' ? inner : Array.isArray(inner) ? inner.join('') : '';
    return { language, code };
  }
  return { language: '', code: '' };
}
